import axios from 'axios';
import { nestCall, NestApiType } from '@/composables/nestApi';
import { formatDate } from '@/helpers/Date';
import type { OwnerIntegrationInfo } from '@/types/integrations';

export enum SchemaType {
  System = 'system',
  Custom = 'custom',
}

export interface SchemaItem {
  id_custom_object?: number;
  type: string;
  schema_name: string;
  description?: string;
  unique_keys?: string[];
  last_date_sync: string | null;
  schema_type: SchemaType;
  active: boolean;
  fields?: any;
  relationships?: any[];
  table_name?: string;
  // Forwarded from the backend listing enrichment so the listing badge
  // can render. `null` when the schema was created by a human user.
  owner_integration?: OwnerIntegrationInfo | null;
}

export interface RelationDTO {
  schema_type: 'system' | 'custom';
  schema: string;
  field: string;
}

export interface SaveFieldCustomDataDTO {
  name: string;
  type: string;
  description?: string;
  required: boolean;
  default?: any;
  relation?: RelationDTO | null;
  options?: string[];
}

export interface SaveCustomDataDefinitionsDTO {
  name: string;
  unique_keys?: string[];
  description?: string;
  fields: SaveFieldCustomDataDTO[];
}

// Cache for fetchCustomSchemas — avoids redundant API calls when multiple
// components/themes request the same data within a short time window.
const CUSTOM_SCHEMAS_CACHE_TTL = 30_000; // 30 seconds
let customSchemasCache: SchemaItem[] | null = null;
let customSchemasCacheTimestamp = 0;
let customSchemasCachePromise: Promise<SchemaItem[]> | null = null;

/**
 * Invalidate the custom schemas cache.
 * Call this after any mutation (create, update, activate, deactivate, delete).
 */
export function invalidateCustomSchemasCache(): void {
  customSchemasCache = null;
  customSchemasCacheTimestamp = 0;
  customSchemasCachePromise = null;
}

export async function fetchCustomSchemas(): Promise<SchemaItem[]> {
  // Return cached data if still fresh
  if (customSchemasCache && (Date.now() - customSchemasCacheTimestamp) < CUSTOM_SCHEMAS_CACHE_TTL) {
    return customSchemasCache;
  }

  // Deduplicate concurrent calls: if a fetch is already in flight, share its promise
  if (customSchemasCachePromise) {
    return customSchemasCachePromise;
  }

  customSchemasCachePromise = fetchCustomSchemasFromApi();

  try {
    const result = await customSchemasCachePromise;
    customSchemasCache = result;
    customSchemasCacheTimestamp = Date.now();
    return result;
  } finally {
    customSchemasCachePromise = null;
  }
}

async function fetchCustomSchemasFromApi(): Promise<SchemaItem[]> {
  const allCustomObjects: SchemaItem[] = [];

  try {
    const limit = 10;
    let offset = 0;
    let hasNextPage = true;
    while (hasNextPage) {
      // Build API params according to Swagger specification: order and filter must be arrays
      const params = {
        limit,
        offset,
        order: [
          { field: 'created_at', type: 'desc' },
        ],
        filters: [
          { field: 'status', operator: 'IN', values: ['active', 'inactive'] },
        ],
      };

      // eslint-disable-next-line no-await-in-loop
      const response = await nestCall('get', NestApiType.CLIENT_API, '/custom-data-definitions', {}, '', undefined, true, params);

      if (response && response.error) {
        console.error('API Error:', response.error);
        return [];
      }

      if (!response || !response.data) {
        return [];
      }

      const data = response;
      const pageCustomObjects: SchemaItem[] = data.data
        // Removed .filter((item: any) => item.active === true) to fetch all custom schemas
        .map((item: any): SchemaItem => ({
          // The listing now exposes the id as `id_definition` (aligned with
          // get/create + the documented contract). Fall back to the legacy raw
          // column so the UI keeps working whatever the deploy order.
          id_custom_object: item.id_definition ?? item.id_custom_data_definition,
          type: 'custom-objects',
          schema_name: item.name,
          description: item.description || undefined,
          unique_keys: item.unique_keys,
          table_name: item.table_name,
          last_date_sync: null, // This might be updated later if sync status is available
          schema_type: SchemaType.Custom,
          fields: item.fields,
          relationships: item.relationships,
          active: item.status === 'active', // Map the active status from API
          // Pass through the integration ownership badge data emitted by
          // the backend enrichment. Null for user-created schemas.
          owner_integration: item.owner_integration ?? null,
        }));
      allCustomObjects.push(...pageCustomObjects);
      if (!data.meta.pagination.has_next_page) {
        hasNextPage = false;
      }
      offset += limit;
    }
    return allCustomObjects;
  } catch (error) {
    return [];
  }
}

export async function fetchOverrides(
  target_system_schema: string,
  system_field_name: string,
): Promise<any[]> {
  const allOverrides: any[] = [];

  try {
    const limit = 10;
    let offset = 0;
    let hasNextPage = true;
    while (hasNextPage) {
      // Build API params according to Swagger specification: order and filter must be arrays
      const params = {
        limit,
        offset,
        order: [
          { field: 'created_at', type: 'desc' },
        ],
        filter: [
          { field: 'target_system_schema', operator: '=', value: target_system_schema },
          { field: 'system_field_name', operator: '=', value: system_field_name },
        ],
      };

      // eslint-disable-next-line no-await-in-loop
      const response = await nestCall('get', NestApiType.CLIENT_API, '/custom-data-definitions/overrides', {}, '', undefined, true, params);

      if (response && response.error) {
        console.error('API Error:', response.error);
        return [];
      }

      if (!response || !response.data) {
        return [];
      }

      const data = response;
      const pageOverrides: any[] = data.data || [];
      allOverrides.push(...pageOverrides);
      if (!data.meta.pagination.has_next_page) {
        hasNextPage = false;
      }
      offset += limit;
    }
    return allOverrides;
  } catch (error) {
    return [];
  }
}

// get all overrides for this shop
export async function fetchAllOverrides(): Promise<any[]> {
  const allOverrides: any[] = [];

  try {
    const limit = 50;
    let offset = 0;
    let hasNextPage = true;
    while (hasNextPage) {
      const params = {
        limit,
        offset,
        order: [
          { field: 'priority', type: 'asc' }, // sort by priority to maintain order
          { field: 'target_system_schema', type: 'asc' },
          { field: 'system_field_name', type: 'asc' },
        ],
      };

      // eslint-disable-next-line no-await-in-loop
      const response = await nestCall('get', NestApiType.CLIENT_API, '/custom-data-definitions/overrides', {}, '', undefined, true, params);

      if (response && response.error) {
        console.error('API Error:', response.error);
        return [];
      }

      if (!response || !response.data) {
        return [];
      }

      const data = response;
      const pageOverrides: any[] = data.data || [];
      allOverrides.push(...pageOverrides);
      if (!data.meta.pagination.has_next_page) {
        hasNextPage = false;
      }
      offset += limit;
    }
    return allOverrides;
  } catch (error) {
    return [];
  }
}

// System object types shown on /shop/system-objects, in display order.
// Keys must match the `myShopData.syncs.*` i18n entries and the
// `shop.syncs.<type>` route names; the value is the matching `step_type`
// of `sync_steps` returned by the API, whose naming differs on three of them.
const SYSTEM_SCHEMA_SYNC_STEPS: Record<string, string> = {
  customers: 'customers',
  customers_groups: 'customers_groups',
  products: 'products',
  products_categories: 'products_categories',
  vouchers: 'vouchers',
  carriers: 'orders_carriers',
  manufacturers: 'products_manufacturers',
  orders_status: 'orders_statuses',
  orders: 'orders',
};

export async function fetchSystemSchemas(idShop: number): Promise<SchemaItem[]> {
  // Last successful sync per object (sync_steps, status='completed'), so a
  // partial sync refreshes only the objects it replayed.
  const response = await nestCall('get', NestApiType.DEFAULT, `/shop/last-completed-sync/${idShop}`, {}, '', undefined, true);
  const lastSyncByStep = (response && response.success && response.data?.last_sync_at_by_step) || {};

  return Object.entries(SYSTEM_SCHEMA_SYNC_STEPS).map(([type, stepType]) => {
    const lastSyncAt = lastSyncByStep[stepType];

    return {
      type,
      schema_name: type,
      schema_type: SchemaType.System,
      last_date_sync: lastSyncAt
        ? formatDate(lastSyncAt, 'YYYY-MM-DDTHH:mm:ss.SSSZ', 'YYYY-MM-DD HH:mm:ss')
        : null,
      active: true, // System schemas are always active
    };
  });
}

export const fetchAllSchemasData = async (idShop: number) => {
  let schemas: SchemaItem[] = [];

  try {
    const [systemSchemas, customSchemas] = await Promise.all([
      fetchSystemSchemas(idShop),
      fetchCustomSchemas(),
    ]);

    schemas = [...systemSchemas, ...customSchemas];
  } catch (err: any) {
    schemas = [];
  }

  return schemas;
};

// Envoie les données du schéma custom en POST
export async function saveCustomSchemaData(
  data: SaveCustomDataDefinitionsDTO,
): Promise<{ success: boolean; id_custom_data_definition?: number }> {
  try {
    const response = await nestCall('post', NestApiType.CLIENT_API, '/custom-data-definitions', {}, '', data, true);
    if (response && response.data && response.statusCode === 201) {
      invalidateCustomSchemasCache();
      return {
        success: true,
        id_custom_data_definition: response.data.id_definition,
      };
    }
    return {
      success: false,
    };
  } catch (error) {
    return {
      success: false,
    };
  }
}

// Récupère la définition d'un schéma custom via son id
export async function fetchCustomSchemaDefinition(idDefinition: number) {
  try {
    const response = await nestCall('get', NestApiType.CLIENT_API, `/custom-data-definitions/${idDefinition}`, {}, '', undefined, true);

    if (response && response.error) {
      console.error('API Error:', response.error);
      return {
        success: false,
      };
    }

    if (response && response.statusCode === 200 && response.data) {
      return {
        success: true,
        data: response.data,
      };
    }
    return {
      success: false,
    };
  } catch (error) {
    return {
      success: false,
    };
  }
}

// Nouvelle méthode pour étendre un schéma custom en mode édition
export async function extendCustomSchemaData(data: any, id_custom_data: any): Promise<{ success: boolean; error?: string }> {
  try {
    const response = await nestCall('patch', NestApiType.CLIENT_API, `/custom-data-definitions/${id_custom_data}/extend`, {}, '', data, true);
    if (response && response.statusCode === 200) {
      invalidateCustomSchemasCache();
      return { success: true };
    }
    return { success: false, error: response?.message || '' };
  } catch (error: any) {
    const message = error?.response?.data?.message || error?.message || '';
    return { success: false, error: message };
  }
}

// Activate a custom schema definition
export async function activateCustomSchema(id_definition: number): Promise<boolean> {
  try {
    const response = await nestCall('patch', NestApiType.CLIENT_API, `/custom-data-definitions/${id_definition}/activate`, {}, '', undefined, true);
    if (response && response.statusCode === 200) {
      invalidateCustomSchemasCache();
      return true;
    }
    return false;
  } catch (error) {
    return false;
  }
}

// Deactivate a custom schema definition
export async function deactivateCustomSchema(id_definition: number): Promise<boolean> {
  try {
    const response = await nestCall('patch', NestApiType.CLIENT_API, `/custom-data-definitions/${id_definition}/deactivate`, {}, '', undefined, true);
    if (response && response.statusCode === 200) {
      invalidateCustomSchemasCache();
      return true;
    }
    return false;
  } catch (error) {
    return false;
  }
}

// Delete a custom schema definition
export async function deleteCustomSchema(id_definition: number): Promise<boolean> {
  try {
    const response = await nestCall('delete', NestApiType.CLIENT_API, `/custom-data-definitions/${id_definition}`, {}, '', undefined, true);
    if (response && response.statusCode === 200) {
      invalidateCustomSchemasCache();
      return true;
    }
    return false;
  } catch (error) {
    return false;
  }
}

export const saveOverrides = async (targetSystemSchema: string, systemFieldName: string, data: any) => {
  try {
    const response = await nestCall(
      'put',
      NestApiType.CLIENT_API,
      `/custom-data-definitions/overrides?target_system_schema=${targetSystemSchema}&system_field_name=${systemFieldName}`,
      {},
      '',
      data,
      true,
    );
    if (response && response.statusCode === 200) {
      return true;
    }
    return false;
  } catch (error) {
    return false;
  }
};
